import {
  forwardRef,
  Fragment,
  isValidElement,
  useMemo,
  type ReactNode,
} from "react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Text,
  VStack,
  type ButtonProps,
  type MenuItemProps,
  type MenuListProps,
  type MenuProps,
} from "@chakra-ui/react";
import CheckIcon from "assets/icons/medium/check.svg?react";

export type MenuOption<T = string> = { value: T; label: ReactNode };

export type OptionsGroup<T = string> = {
  label: { id: string; value: string };
  options: MenuOption<T>[];
};

type SelectInputProps<T> = {
  placeholder?: string;
  selectionPrefix?: string;
  optionGroups: OptionsGroup<T>[];
  onChange: (option: T, label: string) => void;
  value?: T | null;
  menuProps?: Omit<MenuProps, "children">;
  menuButtonProps?: ButtonProps;
  overlineTextColor?: string;
  overlineText?: string;
  prefixIcon?: ReactNode;
  transformValueToMenuItemOptionValue?: (value: T) => string;
  rightIcon?: ReactNode;
  menuItemProps?: MenuItemProps;
  menuListProps?: MenuListProps;
};

function defaultTransformValueToMenuItemOptionValue(value: unknown) {
  if (typeof value === "string") {
    return value;
  }

  if (import.meta.env.MODE === "development") {
    throw Error(
      "You passed a non-string value to SelectInput without providing a transformValueToMenuItemOptionValue function. Please provide a function to transform the value to a string for the underlying Chakra UI MenuItem.",
    );
  }

  return String(value);
}

function SelectInputWithRef<T>(
  {
    placeholder = "",
    selectionPrefix = "",
    optionGroups,
    onChange,
    value = null,
    menuProps = {},
    menuItemProps = {},
    menuListProps = {},
    menuButtonProps = {},
    overlineTextColor = "gray.600",
    overlineText,
    prefixIcon,
    rightIcon = null,
    transformValueToMenuItemOptionValue = defaultTransformValueToMenuItemOptionValue,
  }: SelectInputProps<T>,
  ref: React.Ref<HTMLInputElement>,
) {
  const selectedLabel = useMemo(() => {
    if (!value) {
      return "";
    }
    for (const group of optionGroups) {
      const option = group.options.find(
        (option) =>
          transformValueToMenuItemOptionValue(option.value) ===
          transformValueToMenuItemOptionValue(value),
      );
      if (option) {
        return isValidElement(option.label)
          ? option.label.props.children
          : option.label;
      }
    }
    return "";
  }, [optionGroups, value, transformValueToMenuItemOptionValue]);

  return (
    <Menu closeOnSelect={true} {...menuProps}>
      <MenuButton
        as={Button}
        variant={"outline"}
        colorScheme={"gray"}
        rightIcon={!rightIcon ? <ChevronDownIcon /> : undefined}
        {...menuButtonProps}
        ref={ref}
      >
        <VStack align={"flex-start"}>
          {overlineText ? (
            <Text fontSize={"md"} fontWeight={500} color={overlineTextColor}>
              {overlineText}
            </Text>
          ) : null}
          <HStack w={"100%"}>
            <HStack
              justify={{ base: "center", md: "flex-start" }}
              w={"100%"}
              overflowX={"hidden"}
            >
              {prefixIcon ? prefixIcon : null}
              <Text
                lineHeight={"24px"}
                whiteSpace={"normal"}
                noOfLines={1}
                wordBreak={"break-all"}
              >
                {value
                  ? `${selectionPrefix} ${selectedLabel}`
                  : `${placeholder}`}
              </Text>
            </HStack>
            {rightIcon}
          </HStack>
        </VStack>
      </MenuButton>
      <MenuList {...menuListProps}>
        {optionGroups.map((group, idx) => (
          <Fragment key={group.label.id}>
            <MenuGroup
              key={group.label.id}
              title={group.label.value.toUpperCase()}
            >
              {group.options.map((option) => (
                <MenuItem
                  key={transformValueToMenuItemOptionValue(option.value)}
                  value={transformValueToMenuItemOptionValue(option.value)}
                  bg={value === option.value ? "indigo.100" : "inherit"}
                  _hover={{
                    bg: "indigo.100",
                  }}
                  onClick={() => onChange(option.value, group.label.id)}
                  {...menuItemProps}
                >
                  <Flex
                    direction={"row"}
                    align={"center"}
                    justify={"space-between"}
                    flexGrow={1}
                  >
                    {option.label}
                    {value === option.value ? <CheckIcon /> : null}
                  </Flex>
                </MenuItem>
              ))}
            </MenuGroup>
            {idx < optionGroups.length - 1 ? <MenuDivider /> : null}
          </Fragment>
        ))}
      </MenuList>
    </Menu>
  );
}

export const SelectInput = forwardRef(SelectInputWithRef) as <T>(
  props: SelectInputProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => ReturnType<typeof SelectInputWithRef>;
